import { useEffect, useState, type ReactNode } from 'react';
import { GoogleLogoIcon } from '../icons/GoogleLogoIcon';
import { UserIcon } from '../icons/UserIcon';
import { MailIcon } from '../icons/MailIcon';
import { ChevronDownIcon } from '../icons/ChevronDownIcon';

type Account = {
  name: string;
  email: string;
};

type GoogleSignInModalProps = {
  open: boolean;
  appName?: string;
  accounts?: Account[];
  onClose: () => void;
  onContinue: (account: Account) => void;
};

const DEFAULT_ACCOUNTS: Account[] = [{ name: 'Kendrick Wang', email: 'wangkendrick@gmail.com' }];

type View = 'chooser' | 'consent';

export function GoogleSignInModal({
  open,
  appName = 'Flodesk',
  accounts = DEFAULT_ACCOUNTS,
  onClose,
  onContinue,
}: GoogleSignInModalProps) {
  const [view, setView] = useState<View>('chooser');
  const [selected, setSelected] = useState<Account | null>(null);

  useEffect(() => {
    if (open) {
      setView('chooser');
      setSelected(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-[rgba(29,23,22,0.59)]"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        className="relative w-[964px] max-w-[92vw] overflow-hidden rounded-[24px] bg-white shadow-[0_23px_43px_rgba(0,0,0,0.19),0_6px_7px_rgba(0,0,0,0.09),0_2px_1.5px_rgba(0,0,0,0.05),0_1px_0.5px_rgba(0,0,0,0.05)]"
      >
        <header className="flex items-center gap-3 border-b border-[#E0E0E0] px-6 py-4">
          <GoogleLogoIcon />
          <span className="text-[14px] font-normal leading-[20px] text-[#1F1F1F]">
            Sign in with Google
          </span>
        </header>

        {view === 'chooser' && (
          <ChooserView
            appName={appName}
            accounts={accounts}
            onSelect={account => {
              setSelected(account);
              setView('consent');
            }}
            onUseAnotherAccount={onClose}
          />
        )}
        {view === 'consent' && selected && (
          <ConsentView
            appName={appName}
            account={selected}
            onSwitchAccount={() => setView('chooser')}
            onCancel={onClose}
            onContinue={() => onContinue(selected)}
          />
        )}
      </div>
    </div>
  );
}

function ChooserView({
  appName,
  accounts,
  onSelect,
  onUseAnotherAccount,
}: {
  appName: string;
  accounts: Account[];
  onSelect: (account: Account) => void;
  onUseAnotherAccount: () => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-6 px-6 py-8">
      <div className="flex flex-col gap-3">
        <AppMark />
        <h2 className="text-[28px] font-normal leading-[36px] text-[#1F1F1F]">Choose an account</h2>
        <p className="text-[14px] leading-[20px] text-[#1F1F1F]">
          to continue to <GoogleLink>{appName}</GoogleLink>
        </p>
      </div>

      <div className="flex flex-col">
        {accounts.map(account => (
          <button
            key={account.email}
            type="button"
            onClick={() => onSelect(account)}
            className="flex items-center gap-3 border-b border-[#E0E0E0] py-3 text-left transition-colors hover:bg-[#F8F9FA]"
          >
            <Avatar name={account.name} />
            <div className="flex flex-col">
              <span className="text-[14px] font-medium leading-[20px] text-[#1F1F1F]">
                {account.name}
              </span>
              <span className="text-[12px] leading-[16px] text-[#5F6368]">{account.email}</span>
            </div>
          </button>
        ))}
        <button
          type="button"
          onClick={onUseAnotherAccount}
          className="flex items-center gap-3 border-b border-[#E0E0E0] py-3 text-left transition-colors hover:bg-[#F8F9FA]"
        >
          <span className="flex size-8 items-center justify-center rounded-full bg-[#F1F3F4] text-[#5F6368]">
            <UserIcon />
          </span>
          <span className="text-[14px] leading-[20px] text-[#1F1F1F]">Use another account</span>
        </button>
        <p className="pt-4 text-[12px] leading-[18px] text-[#5F6368]">
          Before using this app, you can review {appName}&apos;s{' '}
          <GoogleLink>privacy policy</GoogleLink> and <GoogleLink>terms of service</GoogleLink>.
        </p>
      </div>
    </div>
  );
}

function ConsentView({
  appName,
  account,
  onSwitchAccount,
  onCancel,
  onContinue,
}: {
  appName: string;
  account: Account;
  onSwitchAccount: () => void;
  onCancel: () => void;
  onContinue: () => void;
}) {
  return (
    <div className="flex flex-col gap-6 px-6 py-8">
      <div className="grid grid-cols-2 gap-6">
        <div className="flex flex-col gap-4">
          <AppMark />
          <h2 className="text-[28px] font-normal leading-[36px] text-[#1F1F1F]">
            Sign in to {appName}
          </h2>
          <button
            type="button"
            onClick={onSwitchAccount}
            className="flex w-fit items-center gap-2 rounded-full border border-[#DADCE0] py-1 pl-1 pr-3 transition-colors hover:bg-[#F8F9FA]"
          >
            <Avatar name={account.name} className="size-6" />
            <span className="text-[12px] leading-[18px] text-[#1F1F1F]">{account.email}</span>
            <ChevronDownIcon className="text-[#5F6368]" />
          </button>
        </div>

        <div className="flex flex-col gap-4">
          <p className="text-[14px] leading-[20px] text-[#1F1F1F]">
            Google will allow {appName} to access this info about you
          </p>
          <ScopeRow
            icon={<UserIcon />}
            label={account.name}
            description="Name and profile picture"
          />
          <ScopeRow icon={<MailIcon />} label={account.email} description="Email address" />
          <p className="text-[12px] leading-[18px] text-[#1F1F1F]">
            Review {appName}&apos;s <GoogleLink>Privacy Policy</GoogleLink> and{' '}
            <GoogleLink>Terms of Service</GoogleLink> to understand how {appName} will process and
            protect your data.
          </p>
          <p className="text-[12px] leading-[18px] text-[#1F1F1F]">
            To make changes at any time, go to your <GoogleLink>Google Account</GoogleLink>.
          </p>
          <p className="text-[12px] leading-[18px] text-[#1F1F1F]">
            Learn more about <GoogleLink>Sign in with Google</GoogleLink>.
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <GoogleButton onClick={onCancel}>Cancel</GoogleButton>
        <GoogleButton onClick={onContinue}>Continue</GoogleButton>
      </div>
    </div>
  );
}

function ScopeRow({
  icon,
  label,
  description,
}: {
  icon: ReactNode;
  label: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-[2px] text-[#5F6368]">{icon}</span>
      <div className="flex flex-col">
        <span className="text-[14px] leading-[20px] text-[#1F1F1F]">{label}</span>
        <span className="text-[12px] leading-[18px] text-[#5F6368]">{description}</span>
      </div>
    </div>
  );
}

function GoogleButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full border border-[#DADCE0] px-6 py-2 text-[14px] font-medium leading-[20px] text-[#1A73E8] transition-colors hover:bg-[#F8F9FA]"
    >
      {children}
    </button>
  );
}

function GoogleLink({ children }: { children: ReactNode }) {
  return (
    <a href="#" onClick={e => e.preventDefault()} className="text-[#1A73E8] hover:underline">
      {children}
    </a>
  );
}

function AppMark() {
  return (
    <div className="flex size-8 items-center justify-center rounded-md bg-[#1A73E8] text-white">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M3 6V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3M3 6h10M3 6v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V6"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="8" cy="9.5" r="1.5" fill="currentColor" />
      </svg>
    </div>
  );
}

function Avatar({ name, className = 'size-8' }: { name: string; className?: string }) {
  const initial = name.trim().charAt(0).toUpperCase();
  return (
    <span
      className={`${className} flex items-center justify-center rounded-full bg-[#1F1F1F] text-[12px] font-medium text-white`}
    >
      {initial}
    </span>
  );
}
